import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Database, Smartphone,Server ,Brain, Wrench} from 'lucide-react';
import Me from '../assets/images/me.png';
const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const skills = [
    {
      icon: Code,
      title: "Développement Frontend",
      description: "React, Angular.js, TypeScript, Tailwind CSS, Bootstrap",
      color: "text-blue-600"
    },
    {
      icon: Server,
      title: "Développement Backend",
      description: "Node.js, Python, PHP",
      color: "text-green-600"
    },
    {
      icon: Database,
      title: "Base de données",
      description: "MongoDB, ProstgreSQL,SQL Server, MySQL",
      color: "text-purple-600"
    },
    {
      icon: Smartphone,
      title: "Applications Mobiles",
      description: "JAVA,React Native, Flutter",
      color: "text-red-600"
    },
    {
      icon: Brain,
      title: "Machine Learning",
      description: "CNN, ELM, GAN, RNN",
      color: "text-purple-600"
    },
    {
      icon: Wrench,
      title: "Outils",
      description: "Docker, Git, Camunda, CI/CD",
      color: "text-green-600"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2
            variants={itemVariants}
            className="section-title text-center mb-16"
          >
            À Propos de Moi
          </motion.h2>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              variants={itemVariants}
              className="relative"
            >
              <div className="relative z-10">
                <div className="w-64 h-64 mx-auto bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                  <div className="w-56 h-56 bg-white rounded-full flex items-center justify-center">
                    <img 
                      src={Me} 
                      alt="profil"
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                </div>
              </div>
              
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-3xl opacity-30"
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="space-y-6"
            >
              <h3 className="text-3xl font-bold text-gray-800 mb-4">
                Développeur Full Stack Passionné
              </h3>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Avec plus de 3 ans d'expérience dans le développement web,
                je spécialise dans la création d'applications complètes et modernes.
                Ma passion réside dans la transformation de concepts complexes
                en solutions élégantes et performantes.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                J'adopte une approche axée sur l'utilisateur, en m'assurant que
                chaque ligne de code contribue à une expérience exceptionnelle.
                Toujours en quête d'apprentissage, je reste à la pointe des
                dernières technologies et meilleures pratiques.
              </p>

              <div className="flex flex-wrap gap-3 pt-4">
                {['JavaScript', 'React', 'Node.js', 'Python','JAVA','Flutter', 'PostgreSQL','MongoDB', 'Docker','Machine learning'].map((skill) => (
                  <span
                    key={skill}
                    className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            variants={itemVariants}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {skills.map((skill, index) => (
              <motion.div
                key={skill.title}
                className="card text-center group cursor-pointer"
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center ${skill.color}`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <skill.icon size={24} />
                </motion.div>
                
                <h4 className="text-xl font-semibold text-gray-800 mb-2">
                  {skill.title}
                </h4>
                
                <p className="text-gray-600 text-sm">
                  {skill.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
